import React, { useCallback, useEffect, useState } from 'react';
import './toolbar.css';
import Tooltip from '../../lib/tooltip/Tooltip';
import { useNavigate } from 'react-router-dom';
import ResourcesIcon from '../../assets/resources.svg';
import { onClickAway } from '../../utils/DOM';
import CanvasSearch from '../canvas-search/CanvasSearch';
import NotificationsPanel from '../notifications/NotificationsPanel';
import NotificationsIconButton from '../notifications/NotificationsIconButton';
import { EditorService } from '../../services/editor';
import { IComponentLifecycleProps } from '../../utils/react';
import { InfrastructureResourceType } from '@elyx-code/definitions';

export interface IToolBarProps extends IComponentLifecycleProps {
	project: EditorService;
	ready: boolean;
}

const ToolBar: React.FC<IToolBarProps> = (props) => {
	const navigate = useNavigate();

	const [searchIsOpen, setSearchIsOpen] = useState<boolean>(false);
	const [notificationsIsOpen, setNotificationsIsOpen] =
		useState<boolean>(false);

	const onSearchClick = (e: React.MouseEvent) => {
		e.stopPropagation();

		props.project?.hideEntityCreationMenu();
		const isOpen = searchIsOpen;

		if (isOpen) {
			setSearchIsOpen(false);
			return;
		}

		const searchWindow = document.querySelector('.popup__search');

		onClickAway(searchWindow, () => {
			setSearchIsOpen(false);
		});

		setSearchIsOpen(true);

		// If notifications are open, close them
		if (notificationsIsOpen) {
			setNotificationsIsOpen(false);
		}
	};

	const onNotificationClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		props.project?.hideEntityCreationMenu();
		const isOpen = notificationsIsOpen;

		if (isOpen) {
			setNotificationsIsOpen(false);
			return;
		}

		const searchWindow = document.querySelector('.popup__notifications');

		onClickAway(searchWindow, () => {
			setNotificationsIsOpen(false);
		});

		setNotificationsIsOpen(true);

		// If search are open, close them
		if (searchIsOpen) {
			setSearchIsOpen(false);
		}
	};

	const onPublishClick = async () => {
		props.project?.hideEntityCreationMenu?.();
		await props.project?.test?.close();

		let localEditorBasePath = props.project?.editorBasePath;

		if (!localEditorBasePath) {
			// The project hasn't finished loading yet, but the URL already encodes
			// enough to derive the editor base path. Pattern:
			//   /ws/{workspaceSlug}/editor/{projectId}/{branchIdentifier}/{projectVersion}/...
			// Take everything up to and including {projectVersion} (the 6th segment).
			const segments = window.location.pathname.split('/');
			if (
				segments[1] === 'ws' &&
				segments[3] === 'editor' &&
				segments.length >= 7
			) {
				localEditorBasePath = segments.slice(0, 7).join('/');
			}
		}

		navigate(`${localEditorBasePath}/publication/preview`);
	};

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault();

			if (!searchIsOpen) {
				// @ts-ignore
				onSearchClick(e);
			}
		},
		[props.project, searchIsOpen]
	);

	const closeAllMenus = useCallback((e: KeyboardEvent) => {
		setSearchIsOpen(false);
		setNotificationsIsOpen(false);
	}, []);

	useEffect(() => {
		props.project?.on('global-canvas-search', handleKeyDown);
		props.project?.on('escape-key-press', closeAllMenus);

		return () => {
			props.project?.off('global-canvas-search', handleKeyDown);
			props.project?.off('escape-key-press', closeAllMenus);
		};
	}, [handleKeyDown, closeAllMenus]);

	let editorBasePath = props.project?.editorBasePath;

	if (!editorBasePath) {
		// The project hasn't finished loading yet, but the URL already encodes
		// enough to derive the editor base path. Pattern:
		//   /ws/{workspaceSlug}/editor/{projectId}/{branchIdentifier}/{projectVersion}/...
		// Take everything up to and including {projectVersion} (the 6th segment).
		const segments = window.location.pathname.split('/');
		if (
			segments[1] === 'ws' &&
			segments[3] === 'editor' &&
			segments.length >= 7
		) {
			editorBasePath = segments.slice(0, 7).join('/');
		}
	}

	return (
		<div className="toolbar" onMouseOver={(e) => e.stopPropagation()}>
			<div className="toolbar__section">
				<button
					className="toolbar__button toolbar__primary-button"
					id="publish-project-toolbar-button"
					onClick={onPublishClick}
				>
					<i className="fa-solid fa-flag-checkered" />
					<p>Publish</p>
				</button>
				<Tooltip
					anchorQuerySelector="#publish-project-toolbar-button"
					placement="left"
				>
					{'Launch this project as an app to your users'}
				</Tooltip>
			</div>
			<div className="toolbar__section">
				<button
					className="toolbar__button"
					id="search-toolbar-button"
					onClick={onSearchClick}
					disabled={!props.ready}
				>
					<i className="fa-solid fa-magnifying-glass" />
					<p>Search</p>
				</button>
				<Tooltip
					anchorQuerySelector="#search-toolbar-button"
					placement="left"
				>
					{!props.ready
						? 'Wait for the canvas to load before searching'
						: 'Search the canvas entities'}
				</Tooltip>
				{props.ready && (
					<CanvasSearch
						isOpen={searchIsOpen}
						project={props.project}
					/>
				)}
				<NotificationsIconButton onClick={onNotificationClick} />
				<NotificationsPanel
					project={props.project}
					isOpen={notificationsIsOpen}
				/>
				<button
					className="toolbar__button"
					id="to-the-budget-button"
					onClick={() => {
						props.project?.hideEntityCreationMenu();
						navigate(`${editorBasePath}/costs`);
					}}
				>
					<i className="fa-solid fa-dollar-sign" />
					<p>Costs</p>
				</button>
				<Tooltip
					anchorQuerySelector="#to-the-budget-button"
					placement="left"
				>
					{'Manage and inspect the costs for this project'}
				</Tooltip>
				<button
					className="toolbar__button"
					id="to-resources-button"
					onClick={() => {
						props.project?.hideEntityCreationMenu();
						navigate(`${editorBasePath}/resources`);
					}}
				>
					<img src={ResourcesIcon} alt="Cloud resources" />
					<p>Cloud</p>
				</button>
				<Tooltip
					anchorQuerySelector="#to-resources-button"
					placement="left"
				>
					{
						'Explore the cloud resources and data generated by your project when it is published'
					}
				</Tooltip>
				<button
					className="toolbar__button"
					id="help-toolbar-button"
					onClick={() => {
						props.project?.hideEntityCreationMenu();
						navigate(`${editorBasePath}/help`);
					}}
				>
					<i className="fa-solid fa-question" />
					<p>Help</p>
				</button>
				<Tooltip
					anchorQuerySelector="#help-toolbar-button"
					placement="left"
				>
					{'Learn how to use the product and get help'}
				</Tooltip>
				<button
					className="toolbar__button"
					id="project-config-toolbar-button"
					onClick={() => {
						props.project?.hideEntityCreationMenu();
						navigate(`${editorBasePath}/project-configuration`);
					}}
				>
					<i className="fa-solid fa-gear" />
					<p>Project</p>
				</button>
				<Tooltip
					anchorQuerySelector="#project-config-toolbar-button"
					placement="left"
				>
					{'Project settings'}
				</Tooltip>
			</div>
		</div>
	);
};

export default ToolBar;
